import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {Krk} from '../../../shared/interface/krk';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {RegistrationIdentifier} from '../../../shared/interface/registration-identifier';
import {IdentifierService} from '../../../shared/services/identifier.service';
import {ActivatedRoute, Router} from '@angular/router';
import {InsuranceCompany} from '../../../shared/interface/insurance-company';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {ReportCustomComponent} from "../../report-custom/report-custom.component";
import {
  REPORT_TYPE_IDENTIFIERS,
  REPORT_TYPE_IDENTIFIERS_REGIONAL,
  REPORT_TYPE_REGISTRATION
} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-all-identifiers-list',
  templateUrl: './all-identifiers-list.component.html',
  styleUrls: ['./all-identifiers-list.component.scss']
})
export class AllIdentifiersListComponent implements AfterViewInit, OnDestroy {
  @Input() ofwcaId?: string = null;
  @Input() insuranceCompanyId?: number = null;
  @Input() disableFilters = false;
  @Input() disableBreadcrumb = false;
  @Input() excludeColumns: string[] = null;
  @Input() embeddedView = false;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  identifiers: RegistrationIdentifier[] = [];
  displayedColumns: string[] = ['registration', 'ofwca', 'insuranceCompany', 'status', 'type', 'value',  ];
  totalData = 0;
  typeOnlyKeys: any = undefined;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private identifierService: IdentifierService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.identifierService.listSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<RegistrationIdentifier>) => {
          this.identifiers = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      ofwca: 'string',
      insuranceCompany: 'int[]',
      status: 'int[]',
      in$type: 'int[]',
      contains$value: 'string',
      partner: 'string',
      assetsRegional: 'string[]',
      valueIdentifiers: 'int',
      company: 'int',
      parentPartner: 'string',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.ofwcaId = this.ofwcaId ?? data.ofwcaId ?? null;
        this.insuranceCompanyId = this.insuranceCompanyId ?? data.insuranceCompanyId ?? null;
        this.excludeColumns = this.excludeColumns ?? data.excludeColumns ?? [];

        this.displayedColumns = this.displayedColumns.filter((e: string) => {
          return this.excludeColumns.indexOf(e) === -1;
        });

        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            })
          ;
        } else {
          this.loadListData();
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        if (this.embeddedView) {
          this.loadListData();
        } else {
          this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value
          })]);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.identifierService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(headers = false): any {
    const filters = {...this.initialFilters};
    if (null !== this.ofwcaId) {
      filters.ofwca = this.ofwcaId;
    }
    if (null !== this.insuranceCompanyId) {
      filters.insuranceCompany = this.insuranceCompanyId;
    }

    if (headers){
      filters.headers = 'TU,OFWCA,RAU,Rodzaj,Identyfikator,Aktywny,Status,Zgoda na wiadomości konkursowe,' +
        'Partner,Firma,Email produktowy,Email konkursowy,Zgoda na wiadomości produktowe,Nazwa Partnera do analiz,' +
        'Login Chronie.pl,Login Hiperio,Email,Numer telefonu';
    }
   // filters.emptyvalue = true;

    return filters;
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.identifierService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadReportRegional(): void {
    this.identifierService.downloadCustomReportRegional(this.buildFilters(true))
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  delete(krk: Krk): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć identyfikator?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.identifierService.deleteIdentifier(krk.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto identyfikator', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});

  }

  insuranceCompanyFilterChanged(insuranceCompanies: InsuranceCompany[]): void {
    const registrationIdTypes = [];

    insuranceCompanies.forEach((insuranceCompany: InsuranceCompany) => {
      registrationIdTypes.push(...insuranceCompany.registrationIdTypes);
    });

    if (registrationIdTypes.length > 0) {
      this.typeOnlyKeys = registrationIdTypes;
    } else {
      this.typeOnlyKeys = undefined;
    }
  }
  openReportDialog(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_IDENTIFIERS},
    });
  }

  openReportDialogRegional(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_IDENTIFIERS_REGIONAL},
    });
  }
}
